import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useVisitor } from '../context/VisitorContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDatabase, FiKey, FiSave, FiCheck, FiRefreshCw, FiAlertCircle, FiGlobe, FiServer, FiCpu, FiUser, FiLogOut } = FiIcons;

const Settings = () => {
  const { settings, updateSettings, isLoadingDb, ipDatabase, user, logout } = useVisitor();
  const [formData, setFormData] = useState(settings);
  const [saved, setSaved] = useState(false);
  
  // State for IPs
  const [clientIp, setClientIp] = useState('');
  const [serverIp, setServerIp] = useState('');
  const [isLoadingIp, setIsLoadingIp] = useState(false);

  useEffect(() => {
    detectIps();
  }, []);

  const detectIps = async () => {
    setIsLoadingIp(true);
    try {
      // 1. Detect Client IP (You)
      const clientRes = await fetch('https://api.ipify.org?format=json');
      const clientData = await clientRes.json();
      setClientIp(clientData.ip);

      // 2. Detect Server IP (Hosting Provider) via DNS Lookup
      const hostname = window.location.hostname;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        setServerIp('127.0.0.1 (Localhost)');
      } else {
        const dnsRes = await fetch(`https://dns.google/resolve?name=${hostname}&type=A`);
        const dnsData = await dnsRes.json();
        
        if (dnsData.Answer && dnsData.Answer.length > 0) {
          const ipRecord = dnsData.Answer.find(record => record.type === 1);
          setServerIp(ipRecord ? ipRecord.data : 'DNS lookup failed');
        } else {
          setServerIp('Could not resolve');
        }
      }
    } catch (error) {
      console.error('Error detecting IPs:', error);
      setClientIp('Error');
      setServerIp('Error');
    } finally {
      setIsLoadingIp(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings(formData);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Configure your visitor tracking and database</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Account Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center justify-between">
             <div className="flex items-center space-x-3">
               <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                 <SafeIcon icon={FiUser} className="text-purple-600" />
               </div>
               <div>
                 <h2 className="text-lg font-semibold text-gray-900">Account</h2>
                 <p className="text-sm text-gray-500">{user?.email || 'Not logged in'}</p>
               </div>
             </div>
             {user && (
               <button 
                 type="button" 
                 onClick={logout}
                 className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center bg-red-50 px-3 py-2 rounded-lg"
               >
                 <SafeIcon icon={FiLogOut} className="mr-2" />
                 Log Out
               </button>
             )}
          </div>
        </motion.div>

        {/* System Information */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiServer} className="text-2xl text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">System Network Info</h2>
                <p className="text-sm text-gray-500">Network details for debugging</p>
              </div>
            </div>
            <button 
              type="button" 
              onClick={detectIps}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-1 ${isLoadingIp ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hosting Server IP */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiCpu} className="text-blue-500 mr-2" />
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">APP HOSTING SERVER IP</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-mono font-bold text-gray-900">
                  {isLoadingIp ? 'Resolving...' : serverIp || 'Unknown'}
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                The public IP address where this app is currently hosted ({window.location.hostname}).
              </p>
            </div>

            {/* Client IP */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiGlobe} className="text-gray-500 mr-2" />
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">YOUR CLIENT IP</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-mono font-medium text-gray-700">
                  {isLoadingIp ? 'Detecting...' : clientIp || 'Unknown'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This is your computer's IP address as seen by the internet.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Google Sheets Integration */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiDatabase} className="text-2xl text-green-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">IP Database Connection</h2>
                <p className="text-sm text-gray-500">Connect your Google Sheet to identify companies</p>
              </div>
            </div>
            {ipDatabase.length > 0 && (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center">
                <SafeIcon icon={FiCheck} className="mr-1" />
                {ipDatabase.length.toLocaleString()} Rules Loaded
              </span>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Sheets Public URL
              </label>
              <div className="flex gap-2">
                <input 
                  type="url" 
                  value={formData.googleSheetsUrl} 
                  onChange={(e) => setFormData({...formData, googleSheetsUrl: e.target.value})}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                />
              </div>
            </div>
            
            {/* Status Indicator */}
            {isLoadingDb && (
              <div className="flex items-center text-blue-600 text-sm">
                <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                Syncing database...
              </div>
            )}
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.4 }}
          className="flex justify-end"
        >
          <button 
            type="submit" 
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${saved ? 'bg-green-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            <SafeIcon icon={saved ? FiCheck : FiSave} />
            <span>{saved ? 'Saved & Synced' : 'Save Settings'}</span>
          </button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default Settings;